import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  MarketplaceCollectionItem,
  MarketplaceCollectionItemResponse,
  OrderType,
  useGetCurrentOrganizationUserLazyQuery,
} from "@graphql-generated";
import { Box, Divider, Grid, useMediaQuery, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import AboutProject from "@components/AboutProject/AboutProject";
import CardContainer from "@components/CardContainer/CardContainer";
import HomeBannerComponent from "@components/homeBanner/homeBanner";
import SearchContainer from "@components/SearchContainer";
import SelectedFilter from "@components/SelectedFilter";
import FilterLayout, { CheckboxListData } from "@layouts/filterLayout";
import { CFSHomeItem } from "src/pages/index.page";
import Pagination from "@components/Pagination";
import { saleCollection } from "src/enums/saleCollection.enum";

interface HomePageProps {
  data?: MarketplaceCollectionItemResponse | MarketplaceCollectionItem[];
  homeCfs?: CFSHomeItem;
  projectDetails?: any;
  isProject?: boolean;
  isSlug: boolean;
  openFilter: boolean;
  traitsData: CheckboxListData[];
  searchValue: string[];
  selectedItems: string[][];
  collectionSearchValue: string;
  selectedSort: string;
  isLoading: boolean;
  offset: number;
  totalPage: number;
  totalCount: number;
  onChangeSearch: (event: string, index: number) => void;
  onChangeCheckbox: (event: string, index: number) => void;
  setCounterCount: (event: string, index: number) => void;
  onChangeCollectionSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickFilter: () => void;
  onClickMenu: (event: OrderType, value: string) => void;
  onClickClearAll: (event: boolean) => void;
  onClickRemoveItem: (rowIndex: number, colIndex: number) => void;
  handlePagination: (event: number) => void;
  onClickDone: () => void;
}

const HomeLayout: React.FC<HomePageProps> = ({
  data,
  homeCfs,
  projectDetails,
  isProject,
  openFilter,
  isSlug,
  traitsData,
  searchValue,
  selectedItems,
  collectionSearchValue,
  selectedSort,
  isLoading,
  offset,
  totalPage,
  totalCount,
  onChangeCheckbox,
  setCounterCount,
  onChangeSearch,
  onChangeCollectionSearch,
  onClickFilter,
  onClickMenu,
  onClickClearAll,
  onClickRemoveItem,
  handlePagination,
  onClickDone,
}: HomePageProps): JSX.Element => {
  const router = useRouter();
  const { isAuthenticated } = useAuth0();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  const [routeLoading, setRouteLoading] = useState(false);
  
  const projectDetail =
  homeCfs &&
  homeCfs?.items &&
  homeCfs?.items[0] &&
  homeCfs?.items[0]?.fields?.projectList?.filter((item: any) => item?.fields?.mojitoCollectionId === projectDetails);
  useEffect(() => {
    if (isAuthenticated) {
      getCurrentOrgUser();
    }
  }, [isAuthenticated]);
  const [
    getCurrentOrgUser,
    {
      data: currentOrgdata,
      loading: currentOrgloading,
      error: currentOrgerror,
    },
  ] = useGetCurrentOrganizationUserLazyQuery({
    variables: {
      curOrg: process.env.NEXT_PUBLIC_API_ORGANIZATION_ID,
    },
  });

  if (
    currentOrgdata?.me?.userOrgs[0]?.kycStatus === "None" &&
    localStorage.getItem("path") === "fromLogin"
  ) {
    setRouteLoading(true);
    router.push("/kyc");
    localStorage.removeItem("path");
  }

  const scrollToList = () => {
    // helloRef.current.scrollIntoView({ behavior: 'smooth' });
    document
      .getElementById("cardContainer")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const count = selectedItems?.reduce((acc, row) => acc + row?.length, 0);

  const totalSelectedFilter = selectedItems?.reduce((acc, subArray) => {
    if (subArray?.length > 0) {
        return acc + subArray?.length;
    }
    return acc;
  }, 0);

  return (
    <>
      <div>
        {(homeCfs || projectDetails) && (
          <HomeBannerComponent
            projectDetails={projectDetails}
            cfsItem={homeCfs}
            exploreAction={scrollToList}
          />
        )}
        { (isSlug || (data as MarketplaceCollectionItem[])?.[0]?.mintOnDemandData) && (
          <>
            <Divider sx={{ background: '#dcdcd4' }} />
            <AboutProject projectDetails={projectDetails} cfsItem={homeCfs} />
            <Divider sx={{ background: '#dcdcd4', marginBottom: '70px' }} />
          </>
        ) }
        { isSlug ? (
          <>
            <Box sx={{ marginTop: '40px', marginX: { xs: '12px', sm: '12px', lg: '55px' } }}>
              <SearchContainer
                searchValue={ collectionSearchValue }
                selectedSort={ selectedSort }
                totalSelectedFilter={ totalSelectedFilter }
                onClickMenu={ onClickMenu }
                onChangeSearch={ onChangeCollectionSearch }
                onClickFilter={ onClickFilter } />
            </Box>
            <Grid container sx={{ paddingLeft: { xs: '12px', sm: '12px', lg: openFilter ? '55px' : '0px' }, paddingRight: { xs: '12px', sm: '12px', lg: '0px' } }}>
              { openFilter && (
                <Grid sx={{ marginTop: '24px' }} item xs={ 12 } sm={ 12 } md={ openFilter ? 2 : 12 } lg={ openFilter ? 2 : 12 } xl={ openFilter ? 2 : 12 }>
                  <FilterLayout
                    open={ (isMobile || isTablet) && openFilter }
                    traitsData={ traitsData }
                    searchValue={ searchValue }
                    selectedItems={ selectedItems }
                    onChangeCheckbox={ onChangeCheckbox }
                    setCounterCount={ setCounterCount }
                    onChangeSearch={ onChangeSearch }
                    onClickClearAll={ () => onClickClearAll(true) }
                    onClickDone={ onClickDone }
                    onClose={ onClickFilter } />
                </Grid>
              ) }
              { !(isMobile && openFilter) && isSlug && (
                <Grid item xs={ 12 } sm={ 12 } md={ openFilter ? 10 : 12 } lg={ openFilter ? 10 : 12 } xl={ openFilter ? 10 : 12 }>
                  <SelectedFilter traitsData={ traitsData } totalCount={ totalCount } onClickRemoveItem={ onClickRemoveItem } count={ count } selectedItems={ selectedItems } onClickClearAll={ () => onClickClearAll(false) } />
                  <CardContainer
                    isLoading={ isLoading }
                    isSlug={ isSlug }
                    openFilter={openFilter}
                    data={(data as MarketplaceCollectionItemResponse)?.MarketplaceCollectionItem}
                    name={projectDetail?.[0]?.fields?.projectName} />
                  { (((data as MarketplaceCollectionItemResponse)?.MarketplaceCollectionItem || [])?.length > 0 && !isLoading) && (
                    <Box sx={{ marginBottom: '110px' }}>
                      <Pagination offset={ offset + 1 } total={ totalPage } onHandlePagination={ handlePagination } />
                    </Box>
                  ) }
                </Grid>
              ) }
            </Grid>
          </>
        ) : ( 
          <CardContainer
            isLoading={ isLoading }
            isSlug={ isSlug }
            openFilter={openFilter}
            data={data as MarketplaceCollectionItem[]}
            name={projectDetail?.[0]?.fields?.projectName} 
            artistName={ projectDetail?.[0]?.fields?.artistName }/>
        ) }
        { !isSlug && !(data as MarketplaceCollectionItem[])?.[0]?.mintOnDemandData && (
          <>
            <Divider sx={{ background: '#dcdcd4' }} />
            <AboutProject projectDetails={projectDetails} cfsItem={homeCfs} />
          </>
        ) }
      </div>
    </>
  );
};

export default HomeLayout;
